import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  const lastUpdated = "April 24, 2026";

  return (
    <div className="privacy-policy-page">
      {/* Hero Header */}
      <header className="policy-header bg_cover" style={{ 
        backgroundImage: 'linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(/assets/images/home-one/hero/hero-bg.jpg)' 
      }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center pt-100 pb-100">
              <h1 className="text-white mb-3">Privacy Policy</h1>
              <p className="text-white-50">Last Updated: {lastUpdated}</p>
              <Link to="/" className="theme-btn style-two mt-4">
                <i className="fas fa-arrow-left me-2"></i>Back to Home
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Policy Content */}
      <section className="policy-content pt-80 pb-80">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="policy-text-box bg-white p-4 p-md-5 rounded shadow-sm">
                
                <div className="policy-intro mb-5 pb-4 border-bottom">
                  <p className="lead mb-0">
                    At <strong>FSL HMO</strong>, we are committed to protecting your privacy and ensuring the security of your personal and health information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our HMO infrastructure services.
                  </p>
                  <p className="text-muted small mt-3 mb-0">
                    Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site.
                  </p>
                </div>

                {/* Section 1 */}
                <div className="policy-section mb-5">
                  <h3 className="section-title">1. Information We Collect</h3>
                  <p>We may collect information about you in a variety of ways. The information we may collect depends on whether you are visiting as a guest, a registered partner, or an end-user member.</p>
                  
                  <h4 className="subsection-title">a. Personal Data</h4>
                  <p>Personally identifiable information that you voluntarily provide, such as:</p>
                  <ul className="policy-list">
                    <li>Full name, email address, phone number</li>
                    <li>Company name, job title, business address</li>
                    <li>Demographic information (age, gender, location)</li>
                    <li>Account credentials and authentication data</li>
                  </ul>

                  <h4 className="subsection-title">b. Health & Medical Data</h4>
                  <p>As an HMO infrastructure provider, we may process sensitive health information including:</p>
                  <ul className="policy-list">
                    <li>Medical history, diagnoses, and treatment records</li>
                    <li>Claims data, eligibility information, and benefit usage</li>
                    <li>Prescription history and pharmacy records</li>
                    <li>Biometric data for identity verification (where applicable)</li>
                    <li>Health risk assessments and wellness program data</li>
                  </ul>
                  <div className="alert alert-warning mt-3">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <strong>Note:</strong> Health data is encrypted in transit and at rest, and is only accessed by authorized personnel for processing claims and providing care.
                  </div>

                  <h4 className="subsection-title">c. Automatically Collected Data</h4>
                  <p>Information our servers automatically collect when you access our platform:</p>
                  <ul className="policy-list">
                    <li>IP address, browser type, operating system</li>
                    <li>Access times, pages viewed, and clickstream data</li>
                    <li>Device identifiers and mobile network information</li>
                    <li>Location data (if you enable location services)</li>
                  </ul>
                </div>

                {/* Section 2 */}
                <div className="policy-section mb-5">
                  <h3 className="section-title">2. How We Use Your Information</h3>
                  <p>Having accurate information permits us to provide a smooth, efficient, and customized experience. We may use your information to:</p>
                  <ul className="policy-list">
                    <li>Create, manage, and secure your account</li>
                    <li>Process insurance claims and verify member eligibility</li>
                    <li>Facilitate payments between partners, providers, and members</li>
                    <li>Send administrative information (updates, security alerts, support messages)</li>
                    <li>Respond to customer service requests and technical support tickets</li>
                    <li>Monitor platform usage and analyze trends to improve our services</li>
                    <li>Prevent fraudulent transactions and protect against security threats</li>
                    <li>Comply with legal obligations and regulatory requirements in Nigeria and other jurisdictions</li>
                    <li>With your consent, send marketing communications about new features or partner offers</li>
                  </ul>
                </div>

                {/* Section 3 */}
                <div className="policy-section mb-5">
                  <h3 className="section-title">3. Disclosure of Your Information</h3>
                  <p>We may share information we have collected in certain situations:</p>
                  
                  <h4 className="subsection-title">a. With Your Consent</h4>
                  <p>We may disclose your information where you have explicitly consented to such disclosure, such as when you authorize a healthcare provider to access your records.</p>

                  <h4 className="subsection-title">b. Third-Party Service Providers</h4>
                  <p>We engage trusted third parties to perform services on our behalf, including:</p>
                  <ul className="policy-list">
                    <li>Payment processors and financial institutions</li>
                    <li>Cloud hosting and data storage providers</li>
                    <li>Analytics and customer support platforms</li>
                    <li>Partner HMO companies and healthcare facilities</li>
                  </ul>
                  <p>All third parties are contractually obligated to protect your data and use it only for the purposes we specify.</p>

                  <h4 className="subsection-title">c. Legal Requirements</h4>
                  <p>We may disclose information if required by law, court order, or government regulation, or to protect the rights, property, or safety of FSL HMO, our users, or the public.</p>

                  <h4 className="subsection-title">d. Business Transfers</h4>
                  <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you via email or prominent notice on our website of any change in ownership or uses of your data.</p>
                </div>

                {/* Section 4 */}
                <div className="policy-section mb-5">
                  <h3 className="section-title">4. Data Security</h3>
                  <p>We implement administrative, technical, and physical security measures designed to protect your personal information:</p>
                  <ul className="policy-list">
                    <li><strong>Encryption:</strong> All sensitive data is encrypted using TLS 1.3 in transit and AES-256 at rest</li>
                    <li><strong>Access Controls:</strong> Role-based access with multi-factor authentication for staff</li>
                    <li><strong>Auditing:</strong> Comprehensive logging and monitoring of all data access</li>
                    <li><strong>Compliance:</strong> Alignment with NDPR (Nigeria Data Protection Regulation) and international healthcare data standards</li>
                  </ul>
                  <p className="text-muted small">
                    <em>While we strive to protect your information, no electronic transmission or storage method is 100% secure. We cannot guarantee absolute security.</em>
                  </p>
                </div>

                {/* Section 5 */}
                <div className="policy-section mb-5">
                  <h3 className="section-title">5. Cookies and Tracking Technologies</h3>
                  <p>We use cookies and similar tracking technologies to:</p>
                  <ul className="policy-list">
                    <li>Remember your preferences and login status</li>
                    <li>Analyze traffic and improve website performance</li>
                    <li>Personalize content and measure advertising effectiveness</li>
                  </ul>
                  
                  <h4 className="subsection-title">Types of Cookies We Use:</h4>
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm">
                      <thead className="table-light">
                        <tr>
                          <th>Type</th>
                          <th>Purpose</th>
                          <th>Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Essential</strong></td>
                          <td>Required for platform functionality and security</td>
                          <td>Session</td>
                        </tr>
                        <tr>
                          <td><strong>Analytics</strong></td>
                          <td>Understand usage patterns and improve services</td>
                          <td>1-24 months</td>
                        </tr>
                        <tr>
                          <td><strong>Functional</strong></td>
                          <td>Remember preferences and personalize experience</td>
                          <td>12 months</td>
                        </tr>
                        <tr>
                          <td><strong>Marketing</strong></td>
                          <td>Deliver relevant ads and measure campaign performance</td>
                          <td>1-24 months</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <p className="mt-3">
                    You can manage cookie preferences through your browser settings. However, disabling essential cookies may limit platform functionality.
                  </p>
                </div>

                {/* Section 6 */}
                <div className="policy-section mb-5">
                  <h3 className="section-title">6. Your Data Protection Rights</h3>
                  <p>Depending on your location, you may have the following rights regarding your personal data:</p>
                  <ul className="policy-list">
                    <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
                    <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
                    <li><strong>Deletion:</strong> Request deletion of your data ("right to be forgotten")</li>
                    <li><strong>Restriction:</strong> Request limitation of how we process your data</li>
                    <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
                    <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
                    <li><strong>Withdraw Consent:</strong> Withdraw consent at any time where processing is consent-based</li>
                  </ul>
                  <p>To exercise these rights, contact us at <a href="mailto:privacy@fslhmo.com">privacy@fslhmo.com</a>. We will respond within 30 days.</p>
                </div>

                {/* Section 7 */}
                <div className="policy-section mb-5">
                  <h3 className="section-title">7. Children's Privacy</h3>
                  <p>Our services are not directed to children under 13 (or 16 in certain jurisdictions). We do not knowingly collect personal information from children. If we learn we have collected such data, we will delete it promptly. Parents or guardians who believe we have such data should contact us immediately.</p>
                </div>

                {/* Section 8 */}
                <div className="policy-section mb-5">
                  <h3 className="section-title">8. International Data Transfers</h3>
                  <p>FSL HMO operates globally. Your information may be transferred to and processed in countries other than your own, including Nigeria, the United Kingdom, and other jurisdictions. We ensure appropriate safeguards are in place, such as standard contractual clauses approved by data protection authorities, to protect your data during international transfers.</p>
                </div>

                {/* Section 9 */}
                <div className="policy-section mb-5">
                  <h3 className="section-title">9. Changes to This Privacy Policy</h3>
                  <p>We may update this Privacy Policy periodically to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of material changes by:</p>
                  <ul className="policy-list">
                    <li>Posting the updated policy on this page with a new "Last Updated" date</li>
                    <li>Sending an email to the address associated with your account</li>
                    <li>Displaying a prominent notice on our platform prior to the change becoming effective</li>
                  </ul>
                  <p>Your continued use of our services after any modification constitutes acceptance of the updated policy.</p>
                </div>

                {/* Section 10 */}
                <div className="policy-section mb-5">
                  <h3 className="section-title">10. Contact Us</h3>
                  <p>If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact our Data Protection Officer:</p>
                  
                  <div className="contact-details mt-4 p-4 bg-light rounded border-start border-4 border-orange">
                    <div className="row">
                      <div className="col-md-6 mb-3 mb-md-0">
                        <p className="mb-1"><strong>FSL HMO Limited</strong></p>
                        <p className="mb-1 text-muted">Plot 8, Providence Street</p>
                        <p className="mb-0 text-muted">Lekki Phase 1, Lagos, Nigeria</p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-1"><i className="fas fa-phone me-2 text-orange"></i><strong>Phone:</strong> +234 907 000 8899</p>
                        <p className="mb-1"><i className="fas fa-envelope me-2 text-orange"></i><strong>Email:</strong> <a href="mailto:privacy@fslhmo.com">privacy@fslhmo.com</a></p>
                        <p className="mb-0"><i className="fas fa-clock me-2 text-orange"></i><strong>Hours:</strong> Mon-Fri, 9am-6pm WAT</p>
                      </div>
                    </div>
                  </div>
                  
                  <p className="mt-4 small text-muted">
                    For complaints regarding data protection, you may also contact the <strong>Nigeria Data Protection Commission (NDPC)</strong>: 
                    <a href="https://ndpc.gov.ng" target="_blank" rel="noreferrer" className="ms-1">ndpc.gov.ng</a>
                  </p>
                </div>

                {/* Signature */}
                <div className="policy-signature pt-4 border-top">
                  <p className="mb-0">
                    <strong>FSL HMO Limited</strong><br />
                    <span className="text-muted small">Registered in Nigeria | RC: XXXXXXX</span>
                  </p>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Table of Contents Sidebar (Desktop) */}
      <div className="policy-toc d-none d-lg-block">
        <div className="position-fixed" style={{ top: '100px', right: '5%', width: '250px' }}>
          <div className="card shadow-sm">
            <div className="card-header bg-orange text-white">
              <h6 className="mb-0"><i className="fas fa-list me-2"></i>On This Page</h6>
            </div>
            <div className="list-group list-group-flush">
              <a href="#section1" className="list-group-item list-group-item-action small">1. Information We Collect</a>
              <a href="#section2" className="list-group-item list-group-item-action small">2. How We Use Your Information</a>
              <a href="#section3" className="list-group-item list-group-item-action small">3. Disclosure of Information</a>
              <a href="#section4" className="list-group-item list-group-item-action small">4. Data Security</a>
              <a href="#section5" className="list-group-item list-group-item-action small">5. Cookies & Tracking</a>
              <a href="#section6" className="list-group-item list-group-item-action small">6. Your Rights</a>
              <a href="#section10" className="list-group-item list-group-item-action small">10. Contact Us</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;